import { Shield, Clock, DollarSign, Award, Truck, Leaf } from "lucide-react";

const WhyChooseUsSection = () => {
  const features = [
    {
      icon: Shield,
      title: "Licensed & Insured",
      description: "Fully licensed and insured for your peace of mind. We carry comprehensive liability coverage.",
    },
    {
      icon: Clock,
      title: "Same-Day Service",
      description: "Need it done fast? We offer same-day stump grinding for most jobs when you call before noon.",
    },
    {
      icon: DollarSign,
      title: "Competitive Pricing",
      description: "Transparent pricing with no hidden fees. We provide detailed quotes so you know exactly what to expect.",
    },
    {
      icon: Award,
      title: "10+ Years Experience",
      description: "Over a decade of stump grinding expertise in Tucson. We've seen it all and can handle any job.",
    },
    {
      icon: Truck,
      title: "Professional Equipment",
      description: "State-of-the-art stump grinders that can access tight spaces and handle stumps of any size.",
    },
    {
      icon: Leaf,
      title: "Eco-Friendly",
      description: "All wood chips can be left as mulch or hauled away. We practice environmentally responsible methods.",
    },
  ];

  const stats = [
    { number: "500+", label: "Stumps Removed" },
    { number: "10+", label: "Years Experience" },
    { number: "100%", label: "Satisfaction Rate" },
    { number: "24hr", label: "Response Time" },
  ];

  return (
    <section id="why-us" className="py-16 lg:py-24 bg-[#F5F5F0]" data-testid="why-choose-us-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-left mb-12 lg:mb-16">
          <div className="accent-line mb-4" />
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1A1A1A] uppercase mb-4">
            Why Choose Our Stump Grinding Service
          </h2>
          <p className="font-body text-lg text-gray-600 max-w-2xl">
            When it comes to stump grinding near me in Tucson, experience and reliability matter. Here's why homeowners and commercial property managers trust us with their stump removal.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {features.map((feature, index) => (
            <div
              key={index}
              className="bg-white border-2 border-[#E5E5E0] p-6 hover:border-[#1A1A1A] transition-colors group"
              data-testid={`feature-card-${index}`}
            >
              <div className="w-12 h-12 bg-[#1A1A1A] flex items-center justify-center mb-4 group-hover:bg-[#FF4D00] transition-colors">
                <feature.icon className="w-6 h-6 text-white" strokeWidth={2} />
              </div>
              <h3 className="font-heading text-lg font-bold text-[#1A1A1A] uppercase mb-2">
                {feature.title}
              </h3>
              <p className="font-body text-sm text-gray-600 leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        {/* Stats Bar */}
        <div className="bg-[#1A1A1A] p-8 lg:p-12 grid grid-cols-2 lg:grid-cols-4 gap-8 relative overflow-hidden noise-bg">
          <div className="absolute inset-0 z-0" />
          {stats.map((stat, index) => (
            <div key={index} className="text-center relative z-10" data-testid={`stat-${index}`}>
              <div className="stat-number mb-2">{stat.number}</div>
              <div className="font-heading text-sm font-semibold text-gray-400 uppercase tracking-wider">
                {stat.label}
              </div>
            </div>
          ))}
        </div>

        {/* Process Steps */}
        <div className="mt-16">
          <h3 className="font-heading text-2xl sm:text-3xl font-bold text-[#1A1A1A] uppercase mb-8 text-center">
            How It Works
          </h3>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: 1, title: "Call or Request Quote", desc: "Reach out for a free estimate" },
              { step: 2, title: "On-Site Assessment", desc: "We evaluate your stump situation" },
              { step: 3, title: "Professional Grinding", desc: "Fast, efficient stump removal" },
              { step: 4, title: "Cleanup & Done", desc: "Your yard restored, ready to use" },
            ].map((item) => (
              <div key={item.step} className="text-center" data-testid={`process-step-${item.step}`}>
                <div className="w-16 h-16 mx-auto bg-[#FF4D00] flex items-center justify-center mb-4">
                  <span className="font-heading text-2xl font-bold text-white">{item.step}</span>
                </div>
                <h4 className="font-heading text-base font-bold text-[#1A1A1A] uppercase mb-2">
                  {item.title}
                </h4>
                <p className="font-body text-sm text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUsSection;
